import React, { useCallback, useRef, useState } from 'react';
import Files from 'react-files';
import { Icon, AssetPreview, Text, notify } from '@shootsta/common-react';
import { Asset, ViewMode } from '../types';
import { getAssetName, getAssetType, getAssetSize } from '../utils/assetUtils';

export interface MediaSectionProps {
  assetId: string;
  asset?: Asset | null;
  addFileToQueue: (payload: any, orgId: string) => Promise<string>;
  organisationId: string;
  hasUpdatePermission: boolean;
  hasDeletePermission: boolean;
  onAssetChange: (assetId: string) => void;
  onSave: () => Promise<void>;
  viewMode: ViewMode;
  commentCount?: number;
  onComment?: () => void;
}

function MediaSection({
  assetId,
  asset,
  addFileToQueue,
  organisationId,
  hasUpdatePermission,
  hasDeletePermission,
  onAssetChange,
  onSave,
  viewMode,
  commentCount = 0,
  onComment
}: MediaSectionProps) {
  const filesRef = useRef<any>(null);
  const [isDropActive, setIsDropActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const hasComments = commentCount > 0;
  const formattedCount = commentCount > 99 ? '99+' : commentCount;

  const handleBrowse = useCallback(() => {
    filesRef.current?.openFileChooser();
  }, []);

  const handleFilesChange = useCallback(
    async (files: File[]) => {
      if (!files.length || !addFileToQueue) return;
      try {
        setIsUploading(true);
        const newAssetId = await addFileToQueue(
          { file: files[0] },
          organisationId
        );
        if (newAssetId) {
          onAssetChange(newAssetId);
          await onSave();
        }
        filesRef.current?.removeFiles?.();
      } catch (error) {
        notify({ type: 'error', message: 'Upload failed. Please try again.' });
      } finally {
        setIsUploading(false);
      }
    },
    [addFileToQueue, organisationId, onAssetChange, onSave]
  );

  const handleDeleteAsset = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      onAssetChange('');
      await onSave();
    },
    [onAssetChange, onSave]
  );

  const canDelete = hasDeletePermission && hasUpdatePermission;
  const assetName = getAssetName(asset);
  const assetType = getAssetType(asset);
  const assetSize = getAssetSize(asset);

  const renderDropZone = () => (
    <div
      className={`relative rounded-lg flex flex-col items-center justify-center transition-colors bg-[#FAFAFB] ${viewMode === 'grid' ? 'py-12 px-4 h-[196px]' : 'py-6 px-4'}`}
    >
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        preserveAspectRatio="none"
      >
        <rect
          x="0.5"
          y="0.5"
          width="calc(100% - 1px)"
          height="calc(100% - 1px)"
          rx="8"
          ry="8"
          fill="none"
          stroke={'#D4D9E2'}
          strokeWidth="1"
          strokeDasharray="8 8"
        />
      </svg>
      <span className="text-[14px] leading-[20px] text-center font-medium text-[#8C96A9] font-poppins">
        {isUploading ? (
          'Uploading...'
        ) : viewMode === 'grid' ? (
          <>
            Drag and drop or upload
            <br />
            from your computer
          </>
        ) : (
          'Drag and drop or upload from your computer'
        )}
      </span>
    </div>
  );

  const renderGridAssetPreview = () => (
    <div className="relative rounded-lg overflow-hidden bg-darkDenim">
      <div className="h-[170px]">
        <AssetPreview
          asset={asset}
          assetId={assetId}
          height="170px"
          width="100%"
          hideFileTypeIcon
          showOriginalImageAsThumbnail
          canPlayVideo
          canPlayAudio
        />
      </div>
      <div className="bg-denim/80 px-3 py-2 flex items-center justify-between">
        <span className="text-xs text-white truncate flex-1 mr-2">
          {assetName || 'Media'}
        </span>
        <div className="flex items-center gap-2">
          {onComment && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onComment();
              }}
              className="relative cursor-pointer"
              title={
                hasComments ? `View ${formattedCount} comments` : 'Add comment'
              }
            >
              <Icon
                name={hasComments ? 'comments' : 'addcomment'}
                size={16}
                color="white"
              />
              {hasComments && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[8px] rounded-full h-3 w-3 flex items-center justify-center font-medium">
                  {formattedCount}
                </span>
              )}
            </button>
          )}
          {canDelete && (
            <Icon
              name="deleteicon"
              size={16}
              color="white"
              onClick={handleDeleteAsset}
            />
          )}
        </div>
      </div>
    </div>
  );

  const renderListAssetPreview = () => (
    <div className="flex items-center gap-4 border border-sterling border-radius-lg">
      <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0 bg-darkDenim">
        <AssetPreview
          asset={asset}
          assetId={assetId}
          height="48px"
          width="48px"
          hideFileTypeIcon
          showOriginalImageAsThumbnail
          canPlayVideo
          canPlayAudio
        />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-sm text-darkDenim truncate block">
          {assetName || 'Media'}
        </span>
      </div>
      {assetType && (
        <span className="text-sm text-nevada flex-shrink-0">{assetType}</span>
      )}
      {assetSize && (
        <span className="text-sm text-nevada flex-shrink-0">{assetSize}</span>
      )}
      {onComment && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onComment();
          }}
          className="p-1.5 hover:bg-russian rounded flex-shrink-0 relative"
          title={
            hasComments ? `View ${formattedCount} comments` : 'Add comment'
          }
        >
          <Icon
            name={hasComments ? 'comments' : 'addcomment'}
            size={18}
            color="regent"
          />
          {hasComments && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] rounded-full h-3.5 w-3.5 flex items-center justify-center font-medium">
              {formattedCount}
            </span>
          )}
        </button>
      )}
      {canDelete && (
        <button
          type="button"
          onClick={handleDeleteAsset}
          className="p-1.5 hover:bg-russian rounded flex-shrink-0"
        >
          <Icon name="trashclosed" size={18} color="regent" />
        </button>
      )}
    </div>
  );

  return (
    <Files
      ref={filesRef}
      onChange={handleFilesChange}
      onDragEnter={() => setIsDropActive(true)}
      onDragLeave={() => setIsDropActive(false)}
      onDrop={() => setIsDropActive(false)}
      clickable={false}
      accepts={['image/*', 'video/*', 'audio/*']}
      multiple={false}
    >
      <div>
        <div className="flex items-center justify-between mb-2">
          <Text
            noMargin
            prefixIcon="medialibrary"
            className="text-gray-700 text-sm font-medium leading-none truncate"
          >
            Media
          </Text>
          <div className="flex items-center gap-2">
            {hasUpdatePermission && (
              <button
                type="button"
                onClick={handleBrowse}
                className="flex items-center gap-1 cursor-pointer"
              >
                <Icon name="attachment" color="monkey" size={15} />
                <Text className="text-monkey">Upload</Text>
              </button>
            )}
          </div>
        </div>
        {!assetId ? (
          renderDropZone()
        ) : viewMode === 'grid' ? (
          renderGridAssetPreview()
        ) : (
          <>
            {renderDropZone()}
            <div className="mt-3">{renderListAssetPreview()}</div>
          </>
        )}
      </div>
    </Files>
  );
}

export default MediaSection;
